import * as Yup from 'yup'
import Tr from 'translations/Translations';
import InputLimits from "./InputLimits";
import moment, {isMoment} from "libs/moment";

const emailRegexp = /^[\w!#$%&'*+/=?^_`{|}~-]+(?:\.[\w!#$%&'*+/=?^_`{|}~-]+)*@(?:[\w](?:[\w-]*[\w])?\.)+[\w](?:[\w-]*[\w])?$/;
const contractorlimits = InputLimits.procedure.contractor

Yup.addMethod(Yup.string, 'noOnlyWhitespaces', function (message) {
    const usedMessage = message ? message : Tr('not_only_whitespaces')
    return this.test('only-whitespace', usedMessage, function (value) {
        if (value != null && value !== undefined) {
            return value.trim().length > 0;
        }
        return true;
    });
});

const maxStringFunc = ({value, max}) => {
    return Tr('too_many_characters', {max})
}

const minStringFunc = ({value, min}) => {
    const number = Math.abs(value.length - min)
    return Tr('too_low_characters', {number})
}

const amountMin = (min, inclusive) => Yup.string().test('amountMin',
    inclusive ? Tr('v_number_min', {min}) : Tr('v_number_min_exclusive', {min}),
    (val) => {
        if (val) {
            const fVal = parseFloat(val);
            return !isNaN(fVal) && inclusive ? min <= fVal : min < fVal;
        }
        return true;
    })

const amountBasicOptional = (min, inclusive) => amountMin(min, inclusive)
    .matches(/^$|^[^-].*$/, Tr('v_positive')).matches(/^$|^[-]?\d+([,.]|([,.]\d{1,2})?)$/, Tr("amount_error"))

const amountBasicRequired = (min, inclusive) => amountMin(min, inclusive)
    .matches(/^[^-].*$/, Tr('v_positive')).matches(/^[-]?\d+([,.]|([,.]\d{1,2})?)$/, Tr("amount_error"))

const amountMaxDecimal100 = (value, noZero = true) => {
    let tr = ""
    const pValue = parseFloat(value)
    if (noZero && pValue <= 0) return Yup.number().positive(Tr("v_positive"));
    else if (pValue > 100) tr = "number_cant_be_bigger_than"
    else tr = "amount_error"
    return Yup.string().matches(/(^\d{1,2}([,.]\d{1,2})?|100)$/, Tr(tr))
}

const stringExactlyBase = (value, message) => Yup.string()
    .matches(value, message)
    .required(Tr('required_field'))
    .nullable();
const stringNotOneOfBase = (value, message) => Yup.string()
    .notOneOf(value, message)
    .required(Tr('required_field'))
    .nullable();

const stringMinAndMax = (min, max) => Yup.string()
    .min(min, minStringFunc)
    .max(max, maxStringFunc)
    .required(Tr('required_field'));

const vatinPL = (val, optional) => {
    if (!val && optional)
        return true;
    if (val?.length !== 10) {
        return false;
    }
    if (val.charAt(0) === '0' || val.charAt(2) === '0') {
        return false;
    }

    let weight = [6, 5, 7, 2, 3, 4, 5, 6, 7];
    let sum = 0;
    let controlNumber = parseInt(val.substring(9, 10));
    let weightCount = weight.length;
    for (let i = 0; i < weightCount; i++) {
        sum += (parseInt(val.substr(i, 1)) * weight[i]);
    }

    return sum % 11 === controlNumber;
}

const peselPL = (val) => {
    if (val?.length !== contractorlimits.PESEL_PL) {
        return false;
    }

    let weight = [1, 3, 7, 9, 1, 3, 7, 9, 1, 3];
    let sum = 0;
    let controlNumber = parseInt(val.substring(10, 11));
    for (let i = 0; i < weight.length; i++) {
        sum += ((parseInt(val.substring(i, i + 1)) * weight[i]) % 10);
    }
    sum = sum % 10;
    return (10 - sum) % 10 === controlNumber;
}

const dateOrDateTimeValid = () => {
    return Yup.mixed().test('dateOrDateTimeValid', Tr('invalid_date_format'), (value) => {
        if (!value) {
            return true;
        }
        if (isMoment(value)) {
            return value.isValid();
        } else if (typeof value === 'string' || value instanceof String) {
            return moment(value, "YYYY-MM-DD[T]HH:mm:ssZ", true).isValid() || moment(value, "YYYY-MM-DD[T]HH:mm:ss.SSSZ", true).isValid();
        }
        return false;
    })
};

const dateMax = (date, tr) => {
    return dateOrDateTimeValid().test('dateValidMax', tr, (value) => {
        if (!value) {
            return true;
        }

        let dateToCompare = date;
        if (!isMoment(date)) {
            dateToCompare = moment(date);
        }
        let result;
        if (!dateToCompare.isValid()) {
            result = false;
        } else {
            result = !dateToCompare.endOf('day').isBefore(value);
        }

        return result;
    })
}


const dateMin = (date, tr) => {
    return dateOrDateTimeValid().test('dateValidMin', tr, (value) => {
        if (!value || !value.isValid()) {
            return true;
        }
        let dateToCompare = date;
        if (!isMoment(date)) {
            dateToCompare = moment(date);
        }
        let result;
        if (!dateToCompare.isValid()) {
            result = false;
        } else {
            result = !value.isBefore(dateToCompare.startOf("day"));
        }
        return result;
    })
}


const dateTimeMin = (date, tr) => {
    return dateOrDateTimeValid().test('dateTimeMin', tr, (value) => {
        if (!value || !value.isValid()) {
            return true;
        }
        let dateToCompare = date;
        if (!isMoment(date)) {
            dateToCompare = moment(date);
        }
        let result;
        if (!dateToCompare.isValid()) {
            result = false;
        } else {
            result = !value.isBefore(moment(dateToCompare));
        }
        return result;
    })
}
const dateTimeMax = (date, tr) => {
    return dateOrDateTimeValid().test('dateTimeMax', tr, (value) => {
        if (!value || !value.isValid()) {
            return true;
        }
        let dateToCompare = date;
        if (!isMoment(date)) {
            dateToCompare = moment(date);
        }
        let result;
        if (!dateToCompare.isValid()) {
            result = false;
        } else {
            result = !dateToCompare.isBefore(value);
        }
        return result;
    })
}


export default {
    stringNoRq: () => Yup.string().nullable(),
    string: (value, defTrKey) => {
        let trKey = defTrKey ? defTrKey : 'required_field'
        if (!defTrKey && value !== undefined) {
            trKey = 'v_string_required'
        }
        return Yup.string()
            .required(Tr(trKey, {value}))
            .noOnlyWhitespaces()
            .nullable()
    },
    stringExactly: (value, message) => stringExactlyBase(value, message),
    stringNotOneOf: (value, message) => stringNotOneOfBase(value, message),
    stringExactlyMax: (value, message, max) => stringExactlyBase(value, message)
        .max(max, maxStringFunc),
    stringMax: (max) => Yup.string()
        .max(max, maxStringFunc)
        .noOnlyWhitespaces().nullable()
        .required(Tr('required_field')),
    stringMin: (min) => Yup.string()
        .min(min, minStringFunc)
        .noOnlyWhitespaces()
        .required(Tr('required_field')),
    stringMinAndMax,
    stringMaxNoRq: (max) => Yup.string()
        .max(max, maxStringFunc).nullable(),
    number: (value) => {
        if (value !== undefined) {
            return Yup.number()
                .typeError(Tr('v_number_value', {value}))
                .min(0, Tr('v_positive_value', {value}))
                .required(Tr('v_number'))
        }
        return Yup.number()
            .typeError(Tr('v_number'))
            // .integer(Tr('v_integer'))
            .min(0, Tr('v_positive'))
            .required(Tr('v_number'))
    },
    numberMax: (value, key) => Yup.number()
        .typeError(Tr('v_number'))
        .min(0, Tr('v_positive'))
        .max(value, Tr(key ? key : 'v_number_max', {value}))
        .required(Tr('v_number')),
    numberMin: (value, key) => Yup.number()
        .typeError(Tr('v_number'))
        .min(value, Tr(key ? key : 'v_number_min', {min: value}))
        .required(Tr('v_number')),
    numberMin0: () => Yup.number()
        .typeError(Tr('v_number'))
        .min(0, Tr('v_positive'))
        .required(Tr('v_number')),
    numberMaxNoZero: (value, key) => Yup.number()
        .typeError(Tr('v_number'))
        .positive(Tr('v_positive'))
        .max(value, Tr(key ? key : 'v_number_max', {value}))
        .required(Tr('v_number')),
    numberPositiveOptional: () => Yup.number()
        .typeError(Tr('v_number'))
        .positive(Tr('v_positive')).nullable(),
    numberPositiveNoZero: () => Yup.number()
        .typeError(Tr('v_number'))
        .positive(Tr('v_positive'))
        .required(Tr('v_number')),
    numberMaxMin: (min, max, keyMin, keyMax, fieldName) => Yup.number()
        .typeError(fieldName ? Tr('v_number_value_field', {value: fieldName}) : Tr('v_number'))
        .min(min, Tr(keyMin ? keyMin : 'v_number_min', {min}))
        .max(max, Tr(keyMax ? keyMax : 'v_number_max', {value: max}))
        .required(Tr('v_number')),
    decimalNumber: (value) => Yup.string()
        .matches(/^[-]?\d+([,.]|([,.]\d{1,2})?)$/, Tr('decimal_number', {value}))
        .required(Tr('v_number_value_field', {value})).nullable(),
    bigDecimal: () => Yup.string()
        .matches(/^[-]?\d{1,17}([,.]|([,.]\d{1,10})?)$/, Tr('v_number'))
        .required(Tr('required_field')).nullable(),
    bigDecimalOptional: () => Yup.string()
        .matches(/^[-]?\d{1,17}([,.]|([,.]\d{1,10})?)$/, Tr('v_number'))
        .nullable(),
    integerMin: (value = 0) => Yup.number()
        .typeError(Tr('v_number'))
        .min(value, Tr('v_number_min',{min: value}))
        .required(Tr('v_number'))
        .integer(Tr('integer_number')),
    castIntegerMin: (value = 0) => Yup.number()
        .nullable()
        .typeError(Tr('v_number'))
        .min(value, Tr('v_number_min',{min: value}))
        .required(Tr('v_number'))
        .integer(Tr('integer_number')),
    castIntegerMinNoReq: (value = 0) => Yup.number()
        .nullable()
        .typeError(Tr('v_number'))
        .min(value, Tr('v_number_min',{min: value}))
        .integer(Tr('integer_number')),
    integerMax: (value, key) => Yup.number()
        .typeError(Tr('v_number'))
        .min(1, Tr('v_number_min_0'))
        .max(value, Tr(key ? key : 'v_number_max', {value}))
        .required(Tr('v_number'))
        .integer(Tr('integer_number')),
    integerMinMax: (min, max, keyMin, keyMax) => Yup.number()
        .typeError(Tr('v_number'))
        .min(min, Tr(keyMin ? keyMin : min === 0 ? 'v_number_min_0' : 'v_number_min', {min: min}))
        .max(max, Tr(keyMax ? keyMax : 'v_number_max', {value: max}))
        .required(Tr('v_number'))
        .integer(Tr('integer_number')),
    email: () => Yup.string().email(Tr('invalid_email')).matches(emailRegexp, Tr('invalid_email')),
    emailMax: (max) => Yup.string()
        .max(max, maxStringFunc)
        .email(Tr('invalid_email'))
        .matches(emailRegexp, Tr('invalid_email'))
        .nullable()
        .required(Tr('required_field')),
    emailMaxNoRq: (max) => Yup.string()
        .max(max, maxStringFunc)
        .email(Tr('invalid_email')).matches(emailRegexp, Tr('invalid_email')).nullable(),
    amount: (min = 0, inclusive = true) => amountBasicRequired(min, inclusive)
        .nullable()
        .required(Tr('required_field')),
    amountOptional: (min = 0, inclusive = true) => amountBasicOptional(min, inclusive)
        .nullable(),
    amountMax100NoZero: () => Yup.lazy((value) => amountMaxDecimal100(value)
        .nullable()
        .required(Tr('required_field'))
    ),
    amountMax100: () => Yup.lazy((value) => amountMaxDecimal100(value, false)
        .nullable()
        .required(Tr('required_field'))
    ),
    amountMax100Optional: () => Yup.lazy((value) => amountMaxDecimal100(value, false)
        .nullable()
    ),
    arrayOfShape: (shape) => Yup.array(Yup.object().shape(shape)),
    arrayOfShapeRequired: (shape, tr) => Yup.array(Yup.object().shape(shape)).nullable().min(1, Tr(tr ? tr : 'required_field')),
    arrayOf: (shema) => Yup.array().of(shema),
    object: (shape, fields) => Yup.object().shape(shape, fields),
    lazy: (fn) => Yup.lazy(fn),
    arrayRequired: (tr) => Yup.array().nullable().min(1, Tr(tr ? tr : 'required_field')).test(
        'arrayRequired',
        Tr(tr ? tr : 'required_field'),
        (formValue) => {
            return Array.isArray(formValue) && formValue.length >= 0;
        }
    ),
    any: () => Yup.mixed(),
    anyRequired: () => Yup.mixed().required(Tr('required_field')),
    immediatelyError: (tr) => Yup.mixed().test('immediatelyError', tr, (val) => {
        return false;
    }),
    dateMaxNoRq: (date, tr) => dateMax(date, tr),
    dateMax: (date, tr) => dateMax(date, tr).required(Tr('required_field')),
    dateMinNoRq: (date, tr) => dateMin(date, tr),
    dateMin: (date, tr) => dateMin(date, tr).required(Tr('required_field')),
    dateTimeNoReq: () => dateOrDateTimeValid(),
    dateTimeValid: () => dateOrDateTimeValid().required(Tr('required_field')),
    dateTimeMinNoRq: (date, tr) => dateTimeMin(date, tr),
    dateTimeMin: (date, tr) => dateTimeMin(date, tr).required(Tr('required_field')),
    dateTimeMaxNoRq: (date, tr) => dateTimeMax(date, tr),
    dateTimeMax: (date, tr) => dateTimeMax(date, tr).required(Tr('required_field')),
    vatinPLOptional: () => Yup.string().test('vatinPL', Tr("invalid_vatin"), (val) => {
        return vatinPL(val, true);
    }).nullable(),
    vatinOrPeselPL: (tr) => Yup.string().test('vatinOrPeselPL', tr, (val) => {
        const vatinValid = vatinPL(val);
        const peselValid = peselPL(val);

        return vatinValid || peselValid;

    }),
    requiredBoolean: () => Yup.boolean().required(Tr('required_field')).nullable(),
    mixed: () => Yup.mixed(),
    requiredObject: () => Yup.object().required(Tr('required_field')).nullable(),
    ref: Yup.ref,
    url: () => Yup.string().url(Tr('invalid_url')).nullable().required(Tr('required_field')),
    urlOptional: () => Yup.string().url(Tr('invalid_url')).nullable(),
    urlMax: (max) => Yup.string().max(max, maxStringFunc).url(Tr('invalid_url')).nullable().required(Tr('required_field')),
    urlMaxOptional: (max) => Yup.string().max(max, maxStringFunc).url(Tr('invalid_url')).nullable(),
    phoneOptional: () => Yup.string().matches(/(^[+]\d{2}\s?\d{9}$)|^\d{9}$/, Tr('invalid_telephone')).nullable()
}